import {memo} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';

export interface IconListCardProps {
    title: string;
    items: string[];
    /** Marker repeated beside every item. Attached by the caller so data stays JSX-free. */
    icon?: ReactNode;
    /** Heading level for the card title. Defaults to h3. */
    titleComponent?: 'h3' | 'h4';
    /**
     * `2` splits the list into two balanced columns from `md` up — five items come
     * out 3 + 2 — so a short list stops leaving half the card empty. Always one
     * column on phones, where a second would be too narrow to read.
     */
    columns?: 1 | 2;
}

// Blue-tinted surface, matching the light icon tiles and rate chips elsewhere on the
// page rather than carrying a status colour of its own.
const cardSx = {
    width: '100%',
    height: '100%',
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius['3xl'],
    p: {xs: spacing[24], md: spacing[28]}
} as const;

const titleSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[24],
    mb: spacing[20]
} as const;

const listSx = {
    listStyle: 'none',
    m: 0,
    p: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16]
} as const;

// CSS multi-column rather than a grid: the browser balances the items itself, so the
// split stays even (5 → 3 + 2) whatever the item count, with no per-length maths here.
// `gap` does not apply across columns, so the row rhythm moves onto the items.
const twoColumnListSx = {
    listStyle: 'none',
    m: 0,
    p: 0,
    [`@media (min-width: 900px)`]: {
        columnCount: 2,
        columnGap: spacing[24]
    },
    display: 'block',
    '& > li': {
        // Keeps an item from being split down the middle by the column break.
        breakInside: 'avoid' as const,
        mb: spacing[16]
    },
    '& > li:last-of-type': {mb: 0}
} as const;

const itemSx = {display: 'flex', alignItems: 'flex-start', gap: spacing[12]} as const;

const iconWrapSx = {display: 'flex', flexShrink: 0, lineHeight: 0, mt: spacing[4]} as const;

const itemTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

/**
 * IconListCard (Molecule) — a titled card holding a marked list. A real `<ul>`, so
 * a screen reader announces the item count; the marker is decorative and carries
 * no meaning of its own.
 */
const IconListCard = ({title, items, icon, titleComponent = 'h3', columns = 1}: IconListCardProps) => (
    <Box sx={cardSx}>
        <Typography component={titleComponent} className="font-headers" sx={titleSx}>
            {title}
        </Typography>
        <Box component="ul" sx={columns === 2 ? twoColumnListSx : listSx}>
            {items.map((item) => (
                <Box component="li" key={item} sx={itemSx}>
                    {icon && (
                        <Box aria-hidden sx={iconWrapSx}>
                            {icon}
                        </Box>
                    )}
                    <Typography component="span" className="font-regular-text" sx={itemTextSx}>
                        {item}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

export default memo(IconListCard);
